import { useEffect, useRef } from 'react';
import {
  AppTasksQueryVariables,
  FilterGroupingEnum,
  Task,
  TaskKindEnum,
  TaskFilterInputObject,
  TaskScopeNameEnum,
  useAppTasksCountersQuery,
  useAppTasksQuery,
} from 'graphql-common';
import { CustomTaskScopeNameEnum } from 'constants/enums';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import _get from 'lodash/get';
import { logger } from '@lib/services/logger';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import getTaskCounterData from '@lib/utils/getTaskCounterData';
import { ListQueryHookProps } from '../types';
import {
  addFilter,
  getPaginationAndSorting,
  parseFilterValues,
} from '../helpers';

const createFilters = (
  filterParams: any,
  searchQuery?: string,
): TaskFilterInputObject[] => {
  const filters: TaskFilterInputObject[] = [];

  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'formCategoryName' },
        { fieldName: 'siteName' },
        { fieldName: 'siteAreaName' },
        { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
      ]),
    );
  }

  const periodValue = _get(
    filterParams,
    TasksFilterFieldNames.Period,
    [],
  ) as string[];
  const [periodStart, periodEnd] = periodValue;
  if (periodStart) {
    filters.push({
      deadlineAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { gteq: periodStart },
      },
    });
  }
  if (periodEnd) {
    filters.push({
      deadlineAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { lteq: periodEnd },
      },
    });
  }

  addFilter(
    filters,
    'priority',
    parseFilterValues(_get(filterParams, TasksFilterFieldNames.Priority)),
  );

  addFilter(
    filters,
    'siteId',
    parseFilterValues(_get(filterParams, TasksFilterFieldNames.Site)),
  );

  const siteAreaIdValue = _get(filterParams, TasksFilterFieldNames.SiteArea);
  const assetIdValue = _get(filterParams, TasksFilterFieldNames.Asset);

  addFilter(filters, 'siteAreaId', parseFilterValues(siteAreaIdValue));

  if (assetIdValue && assetIdValue.length > 0) {
    addFilter(filters, 'assetId', parseFilterValues(assetIdValue));
  } else if (siteAreaIdValue && siteAreaIdValue.length > 0) {
    filters.push({
      assetId: {
        grouping: FilterGroupingEnum.And,
        predicate: { null: true },
      },
    });
  }

  addFilter(
    filters,
    'formCategoryId',
    parseFilterValues(_get(filterParams, TasksFilterFieldNames.FormCategory)),
  );

  return filters;
};

export default function useAppTasksQueryHook({
  searchQuery,
  tabParam,
  filterParams,
  paginationParams,
  orderingParams,
  skip: externalSkip = false,
}: ListQueryHookProps) {
  const { page, perPage, orderingField, orderingDirection } =
    getPaginationAndSorting(paginationParams, orderingParams);

  const isOnDemandTab = tabParam === CustomTaskScopeNameEnum.OnDemand;

  // For on-demand tab, map to the backend scope MyTodoAvailableToComplete
  const scopeName = isOnDemandTab
    ? TaskScopeNameEnum.MyTodoAvailableToComplete
    : ((tabParam || '') as TaskScopeNameEnum);

  const baseFilters = createFilters(filterParams, searchQuery);

  const standardKindFilter: TaskFilterInputObject = {
    kind: {
      grouping: FilterGroupingEnum.And,
      predicate: { eq: TaskKindEnum.Standard },
    },
  };
  const onDemandKindFilter: TaskFilterInputObject = {
    kind: {
      grouping: FilterGroupingEnum.And,
      predicate: { eq: TaskKindEnum.OnDemand },
    },
  };

  // Tasks query filters: base filters + kind filter for the active tab
  const tasksFilters = [...baseFilters];
  if (isOnDemandTab) {
    tasksFilters.push(onDemandKindFilter);
  } else if (
    tabParam === TaskScopeNameEnum.MyTodoAvailableToComplete ||
    tabParam === TaskScopeNameEnum.MyTodoUpcoming
  ) {
    tasksFilters.push(standardKindFilter);
  }

  const tasksQueryVariables: AppTasksQueryVariables = {
    page,
    limit: perPage,
    scope: { name: scopeName },
    sorts:
      orderingField && orderingDirection
        ? { [orderingField]: orderingDirection }
        : undefined,
    filters: tasksFilters,
  };

  // Adjust parameters based on scope
  switch (tabParam) {
    case TaskScopeNameEnum.MyTodoAvailableToComplete:
    case TaskScopeNameEnum.MyTodoUpcoming:
      tasksQueryVariables.includeDeadlineAt = true;
      tasksQueryVariables.includeLateFor = true;
      break;
    case TaskScopeNameEnum.MyCompleted:
      tasksQueryVariables.includeCompletedAt = true;
      break;
    default:
      break;
  }

  const skipQuery =
    externalSkip || tabParam === CustomTaskScopeNameEnum.Downloads;

  const rawFilterKeys = Object.keys(filterParams || {})
    .filter((k) => (filterParams as Record<string, unknown>)[k])
    .join(', ');
  const logPayload = JSON.stringify({
    tabParam,
    scopeName,
    filtersCount: tasksFilters.length,
    rawFilterKeys: rawFilterKeys || '(none)',
    siteFilter: parseFilterValues(
      _get(filterParams, TasksFilterFieldNames.Site) as string,
    ).join(', '),
    siteAreaFilter: parseFilterValues(
      _get(filterParams, TasksFilterFieldNames.SiteArea) as string,
    ).join(', '),
    assetFilter: parseFilterValues(
      _get(filterParams, TasksFilterFieldNames.Asset) as string,
    ).join(', '),
    searchQuery,
    page,
    perPage,
    skipQuery,
  });
  const prevLogPayload = useRef('');
  if (prevLogPayload.current !== logPayload) {
    prevLogPayload.current = logPayload;
    logger.debug('Tasks: online query config', JSON.parse(logPayload));
  }

  const tasksQuery = useAppTasksQuery({
    fetchPolicy: 'no-cache',
    variables: tasksQueryVariables,
    skip: skipQuery,
  });
  // Counters query: pass filter variables so Apollo re-fires on changes
  const tasksCountersQuery = useAppTasksCountersQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filters: baseFilters,
      todoFilters: [...baseFilters, standardKindFilter],
      onDemandFilters: [...baseFilters, onDemandKindFilter],
    },
    skip: externalSkip,
  });
  const {
    loading: tasksLoading,
    data,
    previousData,
    refetch: refetchTasks,
    error: tasksError,
  } = tasksQuery;
  const {
    data: countersData,
    previousData: previousCountersData,
    refetch: refetchTasksCounters,
  } = tasksCountersQuery;

  // Refetch counters on tab change (counter filter variables don't depend on
  // the active tab, so Apollo won't re-fire automatically on tab switch).
  // Guard with `externalSkip`: Apollo's imperative `refetch()` bypasses the
  // `skip` flag, so during a Scanner-driven navigation away from
  // /perform-tasks (where `tabParam` mirrors the destination route's tab
  // value) this would otherwise fire counters against the wire.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (externalSkip) return;
    refetchTasksCounters();
  }, [tabParam, refetchTasksCounters, externalSkip]);

  const queryData = data || previousData;
  const taskCounterData = countersData || previousCountersData;
  const tasksFirstLoading = tasksLoading && previousData === undefined;
  const metadata = _get(queryData, 'data.metadata');
  const totalTasksCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  if (!tasksLoading && data && !skipQuery) {
    const tasks = _get(data, 'data.collection', []) as Task[];
    logger.debug('Tasks: online query result', {
      tabParam,
      tasksCount: tasks.length,
      totalTasksCount,
      totalPages,
      hasError: !!tasksError,
    });
  }

  return {
    refetchTasks: () => {
      refetchTasks();
      refetchTasksCounters();
    },
    taskCounterData: getTaskCounterData(taskCounterData),
    tasks: _get(queryData, 'data.collection', []) as Task[],
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalPages,
    totalTasksCount,
  };
}
